import { useEffect } from "react";

/**
 * Prefetch likely-next route chunks during browser idle time so the first
 * navigation off the homepage feels instant. Uses requestIdleCallback (with
 * a setTimeout fallback for Safari) to never block the critical render path.
 *
 * Usage:
 *   useIdlePrefetch([
 *     () => import("@/pages/BlogPage"),
 *     () => import("@/pages/MyBlocksPage"),
 *   ]);
 *
 * Each importer is invoked exactly once per page mount. The browser bundler
 * already memoises dynamic imports, so calling it again later (e.g. on click)
 * is a no-op — the chunk is already in cache.
 */
const useIdlePrefetch = (importers, { delay = 1500 } = {}) => {
  useEffect(() => {
    if (!Array.isArray(importers) || importers.length === 0) return;
    // Skip on slow connections / data-saver mode — be a good citizen.
    const conn = typeof navigator !== "undefined" && navigator.connection;
    if (conn && (conn.saveData || /(2g|slow-2g)/i.test(conn.effectiveType || ""))) return;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      importers.forEach((importer) => {
        try {
          // Fire-and-forget; ignore failures (network blip, code-split miss).
          Promise.resolve()
            .then(importer)
            .catch(() => {});
        } catch {
          /* noop */
        }
      });
    };

    let handle;
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      handle = window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      handle = setTimeout(run, delay);
    }

    return () => {
      cancelled = true;
      if (typeof window !== "undefined" && "cancelIdleCallback" in window && typeof handle === "number") {
        window.cancelIdleCallback(handle);
      } else if (handle) {
        clearTimeout(handle);
      }
    };
    // We intentionally don't watch `importers` for changes — they're static
    // module-level functions and re-running prefetch on every render wastes
    // bandwidth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useIdlePrefetch;
